import { useCallback, useMemo } from 'react'
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import {
  useDashboardStream,
  type DashboardStreamEvent,
  type DashboardStreamStatus,
} from '@/lib/dashboardStream'
import type {
  DashboardActivityEntry,
  DashboardActivityPage,
  DashboardOverview,
  DashboardPeriod,
  DashboardTopItem,
  LivePulse,
  OrderTypeMixSlice,
  PaymentMixSlice,
  SalesTimeseries,
} from '@/types'

// ────────────────────────────────────────────────────────────────────────────
// useDashboardData — single hook that returns everything the redesigned
// admin dashboard needs.
//
// Design goals:
//   - One place to tune React Query options per section. The financial side
//     refetches on focus only (cheap); the live side polls every 15s as a
//     safety net under the SSE stream.
//   - SSE events trigger immediate cache invalidation. Authoritative numbers
//     — and, since F-RPT-N44, the activity feed itself — are fetched over
//     REST so the client never has to interpret aggregates, and a refresh or
//     a second device shows the same feed.
// ────────────────────────────────────────────────────────────────────────────

/** Rows per activity page; "Show earlier" fetches the next page. */
const ACTIVITY_PAGE_SIZE = 25

/**
 * Stream events that change what the activity feed lists. Anything else (table
 * flips, status nudges, online-order chatter) never refetches it.
 */
const ACTIVITY_EVENT_TYPES = new Set(['payment', 'order_voided', 'order_cancelled', 'refund_issued', 'order_completed'])

interface UseDashboardDataOpts {
  period: DashboardPeriod
  /** ISO YYYY-MM-DD when period === 'custom'. */
  from?: string
  to?: string
}

export interface UseDashboardDataResult {
  overview: ReturnType<typeof useQuery<DashboardOverview | undefined>>
  live: ReturnType<typeof useQuery<LivePulse | undefined>>
  timeseries: ReturnType<typeof useQuery<SalesTimeseries | undefined>>
  topItems: ReturnType<typeof useQuery<DashboardTopItem[] | undefined>>
  paymentMix: ReturnType<typeof useQuery<PaymentMixSlice[] | undefined>>
  orderTypeMix: ReturnType<typeof useQuery<OrderTypeMixSlice[] | undefined>>
  activity: {
    entries: DashboardActivityEntry[]
    /** The business day shown; undefined before a day has been opened. */
    businessDate?: string
    isLoading: boolean
    isError: boolean
    hasMore: boolean
    isFetchingMore: boolean
    loadMore: () => void
  }
  streamStatus: DashboardStreamStatus
  refetchAll: () => void
}

export function useDashboardData({ period, from, to }: UseDashboardDataOpts): UseDashboardDataResult {
  const queryClient = useQueryClient()
  const periodKey = useMemo(() => [period, from ?? '', to ?? ''] as const, [period, from, to])

  // ─── Data fetchers ────────────────────────────────────────────────────
  const overview = useQuery<DashboardOverview | undefined>({
    queryKey: ['dashboard', 'overview', ...periodKey],
    queryFn: () => apiClient.getDashboardOverview(period, from, to).then((r) => r.data),
    staleTime: 60_000,
  })

  const live = useQuery<LivePulse | undefined>({
    queryKey: ['dashboard', 'live'],
    queryFn: () => apiClient.getDashboardLive().then((r) => r.data),
    staleTime: 5_000,
    // Polling is a safety net beneath the SSE stream — if the stream drops
    // we still see updates within 15s.
    refetchInterval: 15_000,
  })

  const timeseries = useQuery<SalesTimeseries | undefined>({
    queryKey: ['dashboard', 'timeseries', ...periodKey],
    queryFn: () => apiClient.getDashboardSalesTimeseries(period, from, to).then((r) => r.data),
    staleTime: 60_000,
  })

  const topItems = useQuery<DashboardTopItem[] | undefined>({
    queryKey: ['dashboard', 'top-items', ...periodKey],
    queryFn: () => apiClient.getDashboardTopItems(period, { from, to, limit: 5 }).then((r) => r.data),
    staleTime: 60_000,
  })

  const paymentMix = useQuery<PaymentMixSlice[] | undefined>({
    queryKey: ['dashboard', 'payment-mix', ...periodKey],
    queryFn: () => apiClient.getDashboardPaymentMix(period, from, to).then((r) => r.data),
    staleTime: 60_000,
  })

  const orderTypeMix = useQuery<OrderTypeMixSlice[] | undefined>({
    queryKey: ['dashboard', 'order-type-mix', ...periodKey],
    queryFn: () => apiClient.getDashboardOrderTypeMix(period, from, to).then((r) => r.data),
    staleTime: 60_000,
  })

  const refetchAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  }, [queryClient])

  // ─── Activity feed (F-RPT-N44) ───────────────────────────────────────
  // Server-built for the current business day, paged newest-first by an
  // `at` cursor. The stream only says "something changed"; the rows come
  // from the database, so split tenders fold into one sale and nothing
  // vanishes on reload.
  const activityQuery = useInfiniteQuery({
    queryKey: ['dashboard', 'activity'],
    queryFn: ({ pageParam }) =>
      apiClient.getDashboardActivity(pageParam, ACTIVITY_PAGE_SIZE).then((r) => r.data as DashboardActivityPage),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) =>
      last?.has_more && last.entries.length > 0 ? last.entries[last.entries.length - 1].at : undefined,
    staleTime: 15_000,
    // Safety net under the stream, like `live`.
    refetchInterval: 60_000,
  })

  const handleEvent = useCallback(
    (ev: DashboardStreamEvent) => {
      // F-CTRL-N72: held carts are counter-local signals, not sales activity —
      // no money moved, so neither the figures nor the owner's feed change.
      if (ev.type === 'held_carts_changed') return
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'live'] })

      // The financial queries are scoped to a period — only invalidate when
      // the active period actually contains "now". Custom windows in the past
      // shouldn't refetch on every order. yesterday / day-before are also
      // immutable from the dashboard's perspective.
      if (periodIncludesNow(period)) {
        queryClient.invalidateQueries({ queryKey: ['dashboard', 'overview', ...periodKey] })
        queryClient.invalidateQueries({ queryKey: ['dashboard', 'timeseries', ...periodKey] })
        if (ev.type === 'payment' || ev.type === 'order_completed') {
          queryClient.invalidateQueries({ queryKey: ['dashboard', 'top-items', ...periodKey] })
          queryClient.invalidateQueries({ queryKey: ['dashboard', 'payment-mix', ...periodKey] })
          queryClient.invalidateQueries({ queryKey: ['dashboard', 'order-type-mix', ...periodKey] })
        }
      }

      if (ACTIVITY_EVENT_TYPES.has(ev.type)) {
        queryClient.invalidateQueries({ queryKey: ['dashboard', 'activity'] })
      }
    },
    [queryClient, period, periodKey],
  )

  const streamStatus = useDashboardStream({ onEvent: handleEvent })

  return {
    overview,
    live,
    timeseries,
    topItems,
    paymentMix,
    orderTypeMix,
    activity: {
      entries: activityQuery.data?.pages.flatMap((p) => p?.entries ?? []) ?? [],
      businessDate: activityQuery.data?.pages[0]?.business_date,
      isLoading: activityQuery.isLoading,
      isError: activityQuery.isError,
      hasMore: Boolean(activityQuery.hasNextPage),
      isFetchingMore: activityQuery.isFetchingNextPage,
      loadMore: () => void activityQuery.fetchNextPage(),
    },
    streamStatus,
    refetchAll,
  }
}

// Periods whose `to` boundary is "today, business-tz" — these benefit from
// cache invalidation on every dashboard event because new activity actually
// changes the totals. yesterday / custom windows in the past don't.
function periodIncludesNow(period: DashboardPeriod): boolean {
  return period === 'today' || period === '7d' || period === '30d' || period === 'cw' || period === 'cm'
}
